<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Caisses ({{ totalRows }})</h1>
          <small class="text-muted">Gérez les caisses physiques rattachées au site courant.</small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['cash_registers', 'cash_register_sessions', 'CashRegister', 'CashRegisterSession']" label="Caisses" />

          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvelle caisse</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2">{{ successMessage }}</div>

      <BootstrapDataTable
        :rows="registers"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkUpdateRegisters"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectRegister"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        state-key="cash-registers.list"
        detail-route-name="CashRegisterShow"
        empty-text="Aucune caisse trouvée."
        client-pagination
        @sort-change="handleSortChange"
        @refresh="fetchRegisters"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button type="button" class="btn btn-sm btn-outline-success" :disabled="bulkActionPending || isOverLimit" @click="bulkSetRegisterStatus('activate')">
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" :disabled="bulkActionPending || isOverLimit" @click="bulkSetRegisterStatus('deactivate')">
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-name="{ row }">
          <div class="fw-semibold">{{ row.name }}</div>
          <span v-if="row.is_default" class="badge bg-dark">Par défaut</span>
        </template>

        <template #cell-type="{ row }">
          <span class="badge" :class="row.type === 'system' ? 'bg-info text-dark' : 'bg-secondary'">
            {{ row.type === 'system' ? 'Système' : 'Standard' }}
          </span>
        </template>

        <template #cell-is_active="{ row }">
          <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
            {{ row.is_active ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #cell-open_session="{ row }">
          <span v-if="row.open_session" class="badge bg-warning text-dark">
            {{ row.open_session.user?.name || 'Ouverte' }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell-validated_sales_amount="{ row }">
          {{ $format.currency(row.validated_sales_amount || 0) }}
        </template>

      </BootstrapDataTable>

      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: '520px', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <template #header>
          <h5 class="mb-0">{{ form.id ? 'Modifier la caisse' : 'Nouvelle caisse' }}</h5>
        </template>

        <form class="register-form" @submit.prevent="saveRegister">
          <div class="mb-3">
            <label class="form-label small fw-semibold">Nom *</label>
            <PrimevueInputText v-model.trim="form.name" class="w-100" required />
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Code</label>
            <PrimevueInputText v-model.trim="form.code" class="w-100" placeholder="Ex: CAISSE-2" />
          </div>

          <label class="form-check d-flex align-items-center gap-2 ps-0 mb-2">
            <PrimevueCheckbox v-model="form.is_default" binary :disabled="form.type === 'system'" />
            <span class="form-check-label">Définir comme caisse par défaut</span>
          </label>

          <label class="form-check d-flex align-items-center gap-2 ps-0">
            <PrimevueCheckbox v-model="form.is_active" binary :disabled="form.type === 'system' || form.is_default" />
            <span class="form-check-label">Caisse active</span>
          </label>

          <label v-if="!form.id" class="form-check d-flex align-items-start gap-2 ps-0 mt-3">
            <PrimevueCheckbox
              v-model="form.create_in_all_shops"
              inputId="cashRegisterCreateAllShops"
              binary
            />
            <span class="form-check-label" for="cashRegisterCreateAllShops">
              Créer aussi dans tous les sites actifs
              <small class="d-block text-muted">Les sites qui possèdent déjà une caisse avec le même nom ou code seront ignorés.</small>
            </span>
          </label>
        </form>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSaving" @click="closeDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm"
            :loading="isSaving"
            :disabled="!form.name"
            loading-label="Enregistrement..."
            @click="saveRegister"
          >
            <i class="fa fa-save"></i>
            Enregistrer
          </AppLoadingButton>
        </template>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

export default {
  name: 'CashRegistersView',
  mixins: [bulkActionViewMixin],
  data() {
    return {
      registers: [],
      isLoading: false,
      isSaving: false,
      dialogVisible: false,
      errorMessage: '',
      successMessage: '',
      table: {
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'type', label: 'Type', sortable: true, class: 'text-center' },
        { key: 'is_active', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'open_session', label: 'Session ouverte' },
        { key: 'sessions_count', label: 'Sessions', sortable: true, class: 'text-end' },
        { key: 'validated_sales_amount', label: 'Ventes validées', sortable: true, class: 'text-end' },
      ],
      form: this.emptyForm(),
    };
  },
  mounted() {
    this.fetchRegisters();
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canBulkUpdateRegisters() {
      return hasPermission(this.currentUser, 'cash_registers.update');
    },
    totalRows() {
      return this.registers.length;
    },
  },
  methods: {
    canSelectRegister(register) {
      return register?.type !== 'system'
        && !register?.is_default
        && !register?.open_session;
    },
    async bulkSetRegisterStatus(action) {
      const isActivation = action === 'activate';

      await this.performBulkAction({
        url: `/cash-registers/bulk/${action}`,
        method: 'patch',
        itemLabel: 'caisse',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les caisses' : 'Désactiver les caisses',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} caisses sélectionnées ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchRegisters(),
      });
    },

    emptyForm() {
      return {
        id: null,
        name: '',
        code: '',
        type: 'standard',
        is_default: false,
        is_active: true,
        create_in_all_shops: false,
      };
    },
    async fetchRegisters() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get('/cash-registers');
        const data = response.data?.data || response.data || [];
        this.registers = Array.isArray(data)
          ? data.map((register) => ({
            ...register,
            sessions_count: Number(register.sessions_count || 0),
            sales_count: Number(register.sales_count || 0),
            validated_sales_amount: Number(register.validated_sales_amount || 0),
          }))
          : [];
      } catch (error) {
        this.errorMessage = 'Impossible de charger les caisses.';
      } finally {
        this.isLoading = false;
      }
    },
    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },
    openCreateDialog() {
      this.form = this.emptyForm();
      this.dialogVisible = true;
    },
    openEditDialog(register) {
      this.form = {
        id: register.id,
        name: register.name || '',
        code: register.code || '',
        type: register.type || 'standard',
        is_default: !!register.is_default,
        is_active: !!register.is_active,
        create_in_all_shops: false,
      };
      this.dialogVisible = true;
    },
    closeDialog() {
      if (this.isSaving) return;
      this.dialogVisible = false;
    },
    async saveRegister() {
      if (this.isSaving) return;

      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';

      const payload = {
        name: this.form.name,
        code: this.form.code || null,
        is_default: this.form.type === 'system' ? true : this.form.is_default,
        is_active: this.form.type === 'system' || this.form.is_default ? true : this.form.is_active,
      };
      if (!this.form.id) {
        payload.create_in_all_shops = Boolean(this.form.create_in_all_shops);
      }

      try {
        let response;
        if (this.form.id) {
          response = await this.$api.put(`/cash-registers/${this.form.id}`, payload);
        } else {
          response = await this.$api.post('/cash-registers', payload);
        }

        this.dialogVisible = false;
        this.successMessage = this.creationSuccessMessage(response?.data?.data?.replication_summary);
        await this.fetchRegisters();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Impossible d’enregistrer la caisse.';
      } finally {
        this.isSaving = false;
      }
    },
    creationSuccessMessage(summary) {
      if (!summary) {
        return 'Caisse enregistrée.';
      }

      const created = Number(summary.created_count || 0);
      const skipped = Number(summary.skipped_count || 0);

      return `Caisse enregistrée. Créée dans ${created} autre(s) site(s), ${skipped} site(s) ignoré(s).`;
    },
    async toggleRegister(register) {
      if (!register?.id) return;

      try {
        const action = register.is_active ? 'deactivate' : 'activate';
        await this.$api.patch(`/cash-registers/${register.id}/${action}`);
        await this.fetchRegisters();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Action impossible.';
      }
    },
    async deleteRegister(register) {
      if (!register?.id) return;
      if (!(await this.$confirmAction(`Supprimer la caisse "${register.name}" ?`))) return;

      try {
        await this.$api.delete(`/cash-registers/${register.id}`);
        this.successMessage = 'Caisse supprimée.';
        await this.fetchRegisters();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Suppression impossible.';
      }
    },
  },
};
</script>
